import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import {
  Eye,
  EyeOff,
  Pencil,
} from 'lucide-react'
import ProfileModalShell from '../../components/ProfileModalShell'
import { API_CONFIG } from '../../api/config'
import {
  apiGetUserProfile,
  apiUpdateUserProfile,
  apiUploadUserProfileAvatar,
  apiChangeUserPassword,
  apiCreateUserPassword,
  type UserProfile as ProfileDetails,
} from '../../api/profile/userProfileApi'


const EMPTY_PROFILE: ProfileDetails = { name: '', email: '', phone: '', has_password: true }
const INITIAL_PROFILE = EMPTY_PROFILE

export default function Profile() {
  const [profile, setProfile] = useState<ProfileDetails>(INITIAL_PROFILE)
  const [draftProfile, setDraftProfile] =
    useState<ProfileDetails>(INITIAL_PROFILE)
  const [editingProfile, setEditingProfile] = useState(false)
  const [editingSecurity, setEditingSecurity] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileMessage, setProfileMessage] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [securitySaving, setSecuritySaving] = useState(false)
  const [securityError, setSecurityError] = useState<string | null>(null)
  const [securitySuccess, setSecuritySuccess] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    apiGetUserProfile().then((res) => {
      if (mounted && res.success && res.data) {
        setProfile(res.data)
        setDraftProfile(res.data)
      } else if (mounted && !res.success) {
        setProfileMessage(res.message)
      }
    }).catch(() => {
      if (mounted) setProfileMessage('Failed to load profile.')
    })

    return () => {
      mounted = false
    }
  }, [])

  const handleProfileEdit = () => {
    if (editingProfile) {
      setDraftProfile(profile)
      setProfileMessage(null)
    }
    setEditingProfile((isEditing) => !isEditing)
  }
  
  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setProfileMessage(null)
    try {
      const res = await apiUploadUserProfileAvatar(file)
      if (res.success && res.data?.avatar) {
        setProfile((current) => ({ ...current, avatar: res.data!.avatar }))
        setDraftProfile((current) => ({ ...current, avatar: res.data!.avatar }))
        setProfileMessage(res.message)
      } else {
        setProfileMessage(res.warning || res.message || 'Photo upload failed.')
      }
    } catch {
      setProfileMessage('Photo upload failed.')
    }
  }

  const handleProfileSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setProfileSaving(true)
    setProfileMessage(null)

    const updatedProfile = {
      name: draftProfile.name.trim(),
      email: draftProfile.email.trim(),
      phone: draftProfile.phone.trim(),
    }

    try {
      const res = await apiUpdateUserProfile(updatedProfile)
      if (res.success && res.data) {
        setProfile(res.data)
        setDraftProfile(res.data)
        setProfileMessage(res.message || 'Profile updated successfully!')
      } else {
        setProfileMessage(res.warning || res.message || 'Profile update failed.')
      }
    } catch {
      setProfileMessage('Profile update failed.')
    } finally {
      setProfileSaving(false)
      setEditingProfile(false)
      setTimeout(() => setProfileMessage(null), 4000)
    }
  }

  const handleSecurityEdit = () => {
    setEditingSecurity((isEditing) => !isEditing)
    setSecurityError(null)
    setSecuritySuccess(null)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleSecuritySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editingSecurity || securitySaving) return
    setSecurityError(null)
    setSecuritySuccess(null)

    if (profile.has_password && !currentPassword) {
      setSecurityError('Please enter your current password.')
      return
    }

    if (newPassword !== confirmPassword) {
      setSecurityError('New password and confirmation do not match.')
      return
    }

    if (newPassword.length < 6) {
      setSecurityError('New password must be at least 6 characters.')
      return
    }

    setSecuritySaving(true)
    try {
      const res = profile.has_password
        ? await apiChangeUserPassword({
            currentPassword,
            newPassword,
            confirmPassword,
          })
        : await apiCreateUserPassword({
            newPassword,
            confirmPassword,
          })
      if (res.success) {
        setSecuritySuccess(res.message || 'Password changed successfully!')
        setEditingSecurity(false)
        setProfile((current) => ({ ...current, has_password: true }))
        setDraftProfile((current) => ({ ...current, has_password: true }))
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setTimeout(() => setSecuritySuccess(null), 4000)
      } else {
        setSecurityError(res.warning || res.message || 'Password update failed.')
      }
    } catch {
      setSecurityError('Failed to connect to server. Please try again.')
    } finally {
      setSecuritySaving(false)
    }
  }

  return (
    <ProfileModalShell>
        <section className="w-full">
          <h1 className="font-host text-[18px] font-semibold tracking-[-0.025em] text-[#161616] sm:text-[20px]">
            My Profile
          </h1>

          <div className="mt-2.5 flex min-h-[82px] items-center justify-between rounded-[12px] bg-white px-[clamp(20px,2.6vw,34px)] py-[14px] shadow-[0_1px_2px_rgba(0,0,0,0.015)]">
            <div className="flex min-w-0 items-center gap-[clamp(14px,1.9vw,22px)]">
              <div className="relative grid h-[58px] w-[58px] shrink-0 place-items-center rounded-full bg-[#f3f3f3] sm:h-[64px] sm:w-[64px]">
                 {profile.avatar && (
                  <img
                    src={profile.avatar}
                    alt="Profile"
                    className="absolute inset-0 h-full w-full rounded-full object-cover"
                  />
                )}
                <button
                  type="button"
                  aria-label="Change profile picture"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-0 right-0 grid h-[18px] w-[18px] place-items-center rounded-full bg-[#ff7027] text-white shadow-sm"
                >
                  <Pencil className="h-[9px] w-[9px] stroke-[2.4]" />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  aria-label="Upload profile picture"
                />
              </div>

              <div className="min-w-0">
                <p className="truncate font-host text-[15px] font-medium text-[#202020] sm:text-[16px]">
                  {profile.name}
                </p>
                <p className="mt-1.5 truncate font-host text-[12px] font-normal text-[#1e1e1e] sm:text-[13px]">
                  {profile.email}
                </p>
              </div>
            </div>

           
          </div>

          <form onSubmit={handleProfileSubmit} className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-host text-[14px] font-semibold text-[#151515] sm:text-[15px]">
                Personal Information
              </h2>
              <button
                type="button"
                onClick={handleProfileEdit}
                className="flex items-center gap-1.5 font-host text-[13px] font-medium text-[#383838] transition hover:text-black"
              >
                <Pencil className="h-[14px] w-[14px] stroke-[1.8]" />
                {editingProfile ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div className="overflow-hidden rounded-[12px] bg-white px-[clamp(16px,1.8vw,24px)]">
              <ProfileRow
                label="Name"
                value={draftProfile.name}
                editing={editingProfile}
                onChange={(value) =>
                  setDraftProfile((current) => ({ ...current, name: value }))
                }
              />
              <ProfileRow
                label="Email Address"
                value={draftProfile.email}
                editing={editingProfile}
                onChange={(value) =>
                  setDraftProfile((current) => ({ ...current, email: value }))
                }
              />
              <ProfileRow
                label="Phone number"
                value={draftProfile.phone}
                editing={editingProfile}
                onChange={(value) =>
                  setDraftProfile((current) => ({ ...current, phone: value }))
                }
                last
              />
            </div>

            {profileMessage && (
              <p className="mt-2 text-right font-host text-[13px] text-[#4a8323]">
                {profileMessage}
              </p>
            )}

            {editingProfile && (
              <div className="mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="rounded-full bg-black px-6 py-2.5 font-host text-[13px] font-medium text-white transition hover:bg-[#202020] active:scale-[0.98] disabled:opacity-50"
                >
                  {profileSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            )}
          </form>

          <form onSubmit={handleSecuritySubmit} className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-host text-[14px] font-semibold text-[#151515] sm:text-[15px]">
                Security
              </h2>
              <button
                type="button"
                onClick={handleSecurityEdit}
                className="flex items-center gap-1.5 font-host text-[13px] font-medium text-[#383838] transition hover:text-black"
              >
                <Pencil className="h-[14px] w-[14px] stroke-[1.8]" />
                {editingSecurity ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div className={`rounded-[12px] bg-white px-[12px] pb-[12px] pt-[10px] sm:px-[14px] transition-all ${editingSecurity ? 'ring-1 ring-[#8daa2b]/40 shadow-sm' : ''}`}>
              {profile.has_password && (
                <PasswordField
                  label="Current Password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  visible={showCurrentPassword}
                  onToggleVisibility={() =>
                    setShowCurrentPassword((isVisible) => !isVisible)
                  }
                  onFocus={() => {
                    if (!editingSecurity) setEditingSecurity(true)
                  }}
                  disabled={!editingSecurity}
                />
              )}
              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Enter new password (min. 6 characters)"
                autoComplete="new-password"
                visible={showNewPassword}
                onToggleVisibility={() =>
                  setShowNewPassword((isVisible) => !isVisible)
                }
                onFocus={() => {
                  if (!editingSecurity) setEditingSecurity(true)
                }}
                disabled={!editingSecurity}
              />
              <PasswordField
                label="Confirm Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Confirm new password"
                autoComplete="new-password"
                visible={showConfirmPassword}
                onToggleVisibility={() =>
                  setShowConfirmPassword((isVisible) => !isVisible)
                }
                onFocus={() => {
                  if (!editingSecurity) setEditingSecurity(true)
                }}
                disabled={!editingSecurity}
                last
              />
            </div>

            {securityError && (
              <p className="mt-2 font-host text-[13px] text-[#e03131]">
                {securityError}
              </p>
            )}
            {securitySuccess && (
              <p className="mt-2 font-host text-[13px] text-[#4a8323]">
                {securitySuccess}
              </p>
            )}

            {editingSecurity && (
              <div className="mt-3 flex items-center justify-between gap-4">
                <span className="font-host text-[12px] text-[#757575]">
                  Minimum 6 characters
                </span>
                <button
                  type="submit"
                  disabled={securitySaving}
                  className="rounded-full bg-black px-6 py-2.5 font-host text-[13px] font-medium text-white transition hover:bg-[#202020] active:scale-[0.98] disabled:opacity-50 shadow-sm"
                >
                 {securitySaving
                    ? (profile.has_password ? 'Updating...' : 'Creating...')
                    : (profile.has_password ? 'Update password' : 'Create password')}
                </button>
              </div>
            )}
          </form>
        </section>
    </ProfileModalShell>
  )
}

type ProfileRowProps = {
  label: string
  value: string
  editing: boolean
  onChange: (value: string) => void
  last?: boolean
}

function ProfileRow({
  label,
  value,
  editing,
  onChange,
  last = false,
}: ProfileRowProps) {
  return (
    <label
      className={`flex min-h-[44px] items-center justify-between gap-5 py-2 ${
        last ? '' : 'border-b border-[#d7d7d7]'
      }`}
    >
      <span className="shrink-0 font-host text-[12px] font-normal text-[#1d1d1d] sm:text-[13px]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        readOnly={!editing}
        onChange={(event) => onChange(event.target.value)}
        className={`min-w-0 flex-1 bg-transparent text-right font-host text-[12px] font-medium text-[#222222] outline-none sm:text-[13px] ${
          editing
            ? 'rounded-[5px] bg-[#f7f7f7] px-2 py-1 ring-1 ring-[#d9d9d9] focus:ring-[#9ebd35]'
            : 'cursor-default'
        }`}
      />
    </label>
  )
}

type PasswordFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  visible: boolean
  onToggleVisibility: () => void
  disabled: boolean
  placeholder?: string
  autoComplete?: string
  last?: boolean
  onFocus?: () => void
}

function PasswordField({
  label,
  value,
  onChange,
  visible,
  onToggleVisibility,
  disabled,
  placeholder,
  autoComplete = 'off',
  last = false,
  onFocus,
}: PasswordFieldProps) {
  return (
    <label className={`block ${last ? '' : 'mb-3'}`}>
      <span className="mb-1.5 block font-host text-[12px] font-normal text-[#151515] sm:text-[13px]">
        {label}
      </span>
      <span
        onClick={onFocus}
        className={`flex h-[42px] items-center rounded-[7px] border px-3 transition-colors ${
          disabled
            ? 'border-[#D9D9D9] bg-[#FBFBFB] cursor-pointer hover:border-[#BDBDBD]'
            : 'border-[#bcbcbc] bg-white focus-within:border-[#8daa2b] focus-within:ring-1 focus-within:ring-[#8daa2b]/20'
        }`}
      >
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onChange={(event) => onChange(event.target.value)}
          className={`min-w-0 flex-1 bg-transparent font-host text-[#171717] outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-[#9E9E9E] placeholder:text-[13px] ${
            visible || !value ? 'text-[14px] tracking-normal' : 'text-[16px] tracking-[0.1em]'
          } ${disabled ? 'cursor-pointer' : ''}`}
          aria-label={label}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={onToggleVisibility}
          aria-label={visible ? `Hide ${label}` : `Show ${label}`}
          className="ml-3 grid h-6 w-6 place-items-center text-[#424242] hover:text-black transition-colors disabled:opacity-30 disabled:hover:text-[#424242]"
        >
          {visible ? (
            <Eye className="h-[18px] w-[18px] stroke-[1.9]" />
          ) : (
            <EyeOff className="h-[18px] w-[18px] stroke-[1.9]" />
          )}
        </button>
      </span>
    </label>
  )
}
